import { Camera, Check, DatabaseZap, ImageUp, LoaderCircle, MonitorUp, RotateCcw, Save, Speaker, UserRound, Volume2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AppShell } from "../components/AppShell";
import { CameraOffIdentity } from "../components/CameraOffIdentity";
import { StatusNotice } from "../components/StatusNotice";
import { useAuth } from "../features/auth/AuthContext";
import { api, getApiErrorMessage } from "../lib/api";
import { isRosterAssetKey, rosterAsset, talkRoster, type RosterAssetKey } from "../lib/assets";
import { talkBrand } from "../lib/brand";
import { roleLabel, ui } from "../lib/ui";
import { defaultPreferences, loadPreferences, savePreferences } from "../lib/preferences";
import { avatarUploadContentType } from "../lib/avatar";
import type { TalkPreferences, TalkProfile } from "../lib/types";

function shownMessage(message: string): string {
  const copy: Record<string, string> = {
    "Talk took too long to respond. Try again.": "Ответ шёл слишком долго. Попробуйте ещё раз.",
    "The request was cancelled.": "Запрос отменён.",
    "Talk is unreachable. Check your connection and try again.": "Сервер недоступен. Проверьте соединение и попробуйте ещё раз.",
    "Something went wrong. Try again.": "Что-то пошло не так. Попробуйте ещё раз.",
  };
  const translated = copy[message];
  return translated ? ui(message, translated) : message;
}

export function SettingsPage() {
  const auth = useAuth();
  const accountId = auth.snapshot?.user.id ?? "";
  const [preferences, setPreferences] = useState<TalkPreferences>(() => loadPreferences());
  const [saved, setSaved] = useState(false);
  const [profileRecord, setProfileRecord] = useState<{ accountId: string; profile: TalkProfile } | null>(null);
  const [avatarChoice, setAvatarChoice] = useState<RosterAssetKey | "custom" | "">("");
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const [avatarStatus, setAvatarStatus] = useState("");

  function update(patch: Partial<TalkPreferences>) {
    setPreferences((current) => ({ ...current, ...patch }));
    setSaved(false);
  }

  function save() {
    savePreferences(preferences);
    setSaved(true);
  }

  function reset() {
    setPreferences(defaultPreferences);
    savePreferences(defaultPreferences);
    setSaved(true);
  }

  async function loadProfile(nextAccountId: string, signal?: AbortSignal) {
    setAvatarLoading(true);
    setAvatarError("");
    setAvatarStatus("");
    try {
      const profile = await api.profile.load(signal);
      if (signal?.aborted) return;
      setProfileRecord({ accountId: nextAccountId, profile });
      setAvatarChoice(profile.avatarUrl ? "custom" : profile.assetKey);
    } catch (error) {
      if (!signal?.aborted) setAvatarError(getApiErrorMessage(error));
    } finally {
      if (!signal?.aborted) setAvatarLoading(false);
    }
  }

  useEffect(() => {
    if (auth.loading || !accountId) return;
    const controller = new AbortController();
    api.profile.load(controller.signal).then((profile) => {
      if (controller.signal.aborted) return;
      setProfileRecord({ accountId, profile });
      setAvatarChoice(profile.avatarUrl ? "custom" : profile.assetKey);
      setAvatarError("");
    }).catch((error) => {
      if (!controller.signal.aborted) setAvatarError(getApiErrorMessage(error));
    });
    return () => controller.abort();
  }, [accountId, auth.loading]);

  async function saveAvatar() {
    if (!accountId || !isRosterAssetKey(avatarChoice) || avatarSaving) return;
    setAvatarSaving(true);
    setAvatarError("");
    setAvatarStatus("");
    try {
      const profile = await api.profile.update(avatarChoice);
      setProfileRecord({ accountId, profile });
      setAvatarChoice(profile.assetKey);
      setAvatarStatus(ui(`Avatar saved to your ${talkBrand.profileLabel} profile.`, "Фигура сохранена в профиле."));
    } catch (error) {
      setAvatarError(getApiErrorMessage(error));
    } finally {
      setAvatarSaving(false);
    }
  }

  async function uploadAvatar(file: File) {
    if (!accountId || avatarUploading || avatarSaving) return;
    if (file.size < 1 || file.size > 2 * 1024 * 1024 || !avatarUploadContentType(file)) {
      setAvatarError(ui("Choose a JPG or PNG image no larger than 2 MB.", "Выберите изображение JPG или PNG не больше 2 МБ."));
      setAvatarStatus("");
      return;
    }
    setAvatarUploading(true);
    setAvatarError("");
    setAvatarStatus("");
    try {
      const profile = await api.profile.upload(file);
      setProfileRecord({ accountId, profile });
      setAvatarChoice("custom");
      setAvatarStatus(talkBrand.id === "ctf" ? "Custom avatar uploaded and saved." : ui(`Custom avatar uploaded and saved to your ${talkBrand.profileLabel} profile.`, "Своё изображение загружено и сохранено в профиле."));
    } catch (error) {
      setAvatarError(getApiErrorMessage(error));
    } finally {
      setAvatarUploading(false);
    }
  }

  const currentProfile = profileRecord?.accountId === accountId ? profileRecord.profile : null;
  const avatarDirty = Boolean(currentProfile && isRosterAssetKey(avatarChoice) && (avatarChoice !== currentProfile.assetKey || currentProfile.avatarUrl));
  const previewAsset = avatarChoice === "custom"
    ? currentProfile?.avatarUrl
    : isRosterAssetKey(avatarChoice) ? rosterAsset(avatarChoice) : undefined;

  return (
    <AppShell>
      <main id="main-content" className="content-page settings-page" tabIndex={-1}>
        <header className="content-heading">
          <div>
            <h1>{ui("Talk settings", "Настройки")}</h1>
            <p>{ui(`Your ${talkBrand.productName} identity follows your account. Device, layout, and accessibility preferences stay in this browser.`, "Фигура привязана к вашему аккаунту. Устройства, раскладка и доступность хранятся в этом браузере.")}</p>
          </div>
          <button className="button button--primary" type="button" onClick={save}>
            <Save aria-hidden="true" size={17} />
            {saved ? ui("Browser settings saved", "Настройки браузера сохранены") : ui("Save browser settings", "Сохранить настройки браузера")}
          </button>
        </header>

        <div className="settings-layout">
          <nav aria-label={ui("Settings sections", "Разделы настроек")}>
            <a href="#talk-avatar">{ui("Room identity", "Фигура в комнате")}</a>
            <a href="#devices">{ui("Devices", "Устройства")}</a>
            <a href="#room-defaults">{ui("Room defaults", "Параметры комнаты")}</a>
            <a href="#accessibility">{ui("Accessibility", "Доступность")}</a>
          </nav>

          <div className="settings-sections">
            <section id="talk-avatar" aria-labelledby="talk-avatar-title">
              <div className="settings-section-heading">
                <UserRound aria-hidden="true" />
                <div>
                  <h2 id="talk-avatar-title">{ui("Room identity", "Фигура в комнате")}</h2>
                  <p>{ui("Your saved identity appears when your camera is off and applies the next time you join. It stays separate from other services.", "Сохранённая фигура видна, когда камера выключена, и подставится при следующем входе. От других сервисов она отделена.")}</p>
                </div>
              </div>

              {auth.loading ? (
                <div className="avatar-profile-loading" role="status"><LoaderCircle className="spin" aria-hidden="true" />{ui("Checking your account…", "Проверяем аккаунт…")}</div>
              ) : !auth.snapshot ? (
                <div className="avatar-signin-prompt">
                  <CameraOffIdentity name={ui("Guest", roleLabel("guest"))} avatarUrl={talkBrand.neutralAvatars ? rosterAsset("golden-sun") : undefined} guest />
                  <div><strong>{ui(`Choose an identity with a ${talkBrand.organizationName} account`, "Выберите фигуру в своём аккаунте")}</strong><span>{ui("Guests receive a random room identity when they join. Sign in to choose one and keep it across browsers.", "Гости получают случайную фигуру при входе. Войдите, чтобы выбрать свою и сохранить её во всех браузерах.")}</span></div>
                  <Link className="button button--secondary button--small" to="/login">{ui("Sign in", "Войти")}</Link>
                </div>
              ) : avatarLoading || (!currentProfile && !avatarError) ? (
                <div className="avatar-profile-loading" role="status"><LoaderCircle className="spin" aria-hidden="true" />{ui("Loading your room identity…", "Загружаем вашу фигуру…")}</div>
              ) : avatarError && !currentProfile ? (
                <StatusNotice
                  tone="danger"
                  title={ui(`${talkBrand.productName} identity unavailable`, "Фигура недоступна")}
                  action={<button className="button button--secondary button--small" type="button" onClick={() => void loadProfile(accountId)}>{ui("Retry", "Повторить")}</button>}
                >
                  {shownMessage(avatarError)}
                </StatusNotice>
              ) : currentProfile ? (
                <div className="avatar-profile-editor">
                  <div className="avatar-profile-summary">
                    <CameraOffIdentity large name={auth.snapshot.user.displayName || auth.snapshot.user.handle} avatarUrl={previewAsset} />
                    <div>
                      <strong>{auth.snapshot.user.displayName || auth.snapshot.user.handle}</strong>
                      <span>@{auth.snapshot.user.handle}</span>
                      <small>{ui("Avatar artwork is decorative; your display name remains your room identity.", "Рисунок фигуры — украшение; в комнате вас узнают по имени.")}</small>
                    </div>
                  </div>

                  <div className="avatar-upload">
                    <div>
                      <strong>{ui("Upload your own", "Загрузить свой")}</strong>
                      <span>{ui(`JPG or PNG, up to 2 MB. ${talkBrand.productName} crops it to a square, removes metadata, and stores a safe 512 px copy.`, "JPG или PNG, до 2 МБ. Кадр обрежется до квадрата, метаданные удалятся, сохранится безопасная копия 512 пикселей.")}</span>
                    </div>
                    <div className="avatar-upload__actions">
                      {currentProfile.avatarUrl && avatarChoice !== "custom" ? <button className="button button--secondary" type="button" onClick={() => {
                        setAvatarChoice("custom");
                        setAvatarError("");
                        setAvatarStatus("");
                      }}>{ui("Use uploaded image", "Использовать загруженное")}</button> : null}
                      <label className={`button button--secondary avatar-upload__button${avatarUploading ? " is-busy" : ""}`}>
                        {avatarUploading ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : <ImageUp aria-hidden="true" size={18} />}
                        <span>{avatarUploading ? ui("Processing…", "Обработка…") : currentProfile.avatarUrl ? ui("Replace image", "Заменить изображение") : ui("Upload image", "Загрузить изображение")}</span>
                        <input type="file" accept="image/jpeg,image/png" disabled={avatarUploading || avatarSaving} onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          event.currentTarget.value = "";
                          if (file) void uploadAvatar(file);
                        }} />
                      </label>
                    </div>
                  </div>

                  <fieldset className="avatar-picker">
                    <legend>{ui("Choose your camera-off avatar", "Фигура при выключенной камере")}</legend>
                    <div className="avatar-picker__grid">
                      {talkRoster.map((asset) => {
                        const selected = asset.key === avatarChoice;
                        return (
                          <label className={`avatar-option${selected ? " is-selected" : ""}`} key={asset.key}>
                            <input
                              type="radio"
                              name="talk-avatar"
                              value={asset.key}
                              checked={selected}
                              onChange={() => {
                                setAvatarChoice(asset.key);
                                setAvatarError("");
                                setAvatarStatus("");
                              }}
                            />
                            <span className="avatar-option__art" aria-hidden="true"><img src={rosterAsset(asset.key) ?? asset.avatarUrl} alt="" width="72" height="72" loading="lazy" decoding="async" /></span>
                            <span className="avatar-option__label">{asset.label}</span>
                            {selected ? <span className="avatar-option__check" aria-hidden="true"><Check size={14} /></span> : null}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  {avatarError ? <StatusNotice tone="danger" title={ui("Could not update avatar", "Не удалось обновить фигуру")}>{shownMessage(avatarError)}</StatusNotice> : null}
                  <div className="avatar-profile-actions">
                    <button className="button button--primary" type="button" disabled={!avatarDirty || avatarSaving || avatarUploading} onClick={() => void saveAvatar()}>
                      {avatarSaving ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : <Save aria-hidden="true" size={17} />}
                      {avatarSaving ? ui("Saving avatar…", "Сохраняем фигуру…") : ui("Save avatar", "Сохранить фигуру")}
                    </button>
                    <span className="avatar-profile-status" role="status" aria-live="polite">{avatarStatus}</span>
                  </div>
                </div>
              ) : null}
            </section>

            <section id="devices">
              <div className="settings-section-heading"><Volume2 aria-hidden="true" /><div><h2>{ui("Devices", "Устройства")}</h2><p>{ui("Choose devices during prejoin. Your last selection is saved automatically.", "Устройства выбираются перед входом. Последний выбор сохраняется сам.")}</p></div></div>
              <div className="settings-row"><div><Camera aria-hidden="true" /><span><strong>{ui("Camera starts on", "Камера включена с начала")}</strong><small>{ui("You always see a preview before publishing.", "Перед тем как вас увидят, вы всегда видите предпросмотр.")}</small></span></div><Toggle checked={preferences.cameraEnabled} label={ui("Camera starts on", "Камера включена с начала")} onChange={(value) => update({ cameraEnabled: value })} /></div>
              <div className="settings-row"><div><Volume2 aria-hidden="true" /><span><strong>{ui("Microphone starts on", "Микрофон включён с начала")}</strong><small>{ui("You can join listen-only when permission is denied.", "Если доступ запрещён, можно войти и только слушать.")}</small></span></div><Toggle checked={preferences.microphoneEnabled} label={ui("Microphone starts on", "Микрофон включён с начала")} onChange={(value) => update({ microphoneEnabled: value })} /></div>
              <div className="settings-row"><div><Speaker aria-hidden="true" /><span><strong>{ui("Mirror local preview", "Зеркалить свой предпросмотр")}</strong><small>{ui("Off by default so text and left/right match what other people see.", "По умолчанию выключено: текст и левая с правой стороной совпадают с тем, что видят другие.")}</small></span></div><Toggle checked={preferences.mirrorPreview} label={ui("Mirror local preview", "Зеркалить свой предпросмотр")} onChange={(value) => update({ mirrorPreview: value })} /></div>
            </section>

            <section id="room-defaults">
              <div className="settings-section-heading"><MonitorUp aria-hidden="true" /><div><h2>{ui("Room defaults", "Параметры комнаты")}</h2><p>{ui("Set the first layout and bandwidth behavior for new joins.", "Начальная раскладка и расход трафика для новых входов.")}</p></div></div>
              <label className="settings-select"><span><strong>{ui("Default layout", "Раскладка по умолчанию")}</strong><small>{ui("Tiled fills the room dynamically; presentation still activates automatically for shared screens.", "Плитка сама заполняет комнату; при показе экрана всё равно включается презентация.")}</small></span><select value={preferences.defaultLayout} onChange={(event) => update({ defaultLayout: event.target.value as TalkPreferences["defaultLayout"] })}><option value="tiled">{ui("Dynamic tiled", "Динамическая плитка")}</option><option value="speaker">{ui("Speaker", "Говорящий")}</option><option value="presentation">{ui("Presentation", "Презентация")}</option></select></label>
              <div className="settings-row"><div><DatabaseZap aria-hidden="true" /><span><strong>{ui("Reduced data mode", "Экономия трафика")}</strong><small>{ui("Fewer preview videos, with audio protected first.", "Меньше видео в превью, звук в первую очередь.")}</small></span></div><Toggle checked={preferences.reducedData} label={ui("Reduced data mode", "Экономия трафика")} onChange={(value) => update({ reducedData: value })} /></div>
            </section>

            <section id="accessibility">
              <div className="settings-section-heading"><RotateCcw aria-hidden="true" /><div><h2>{ui("Accessibility", "Доступность")}</h2><p>{ui("Talk also follows your operating-system preferences.", "Интерфейс также следует настройкам вашей операционной системы.")}</p></div></div>
              <div className="settings-row"><div><RotateCcw aria-hidden="true" /><span><strong>{ui("Reduce interface motion", "Меньше движения в интерфейсе")}</strong><small>{ui("Removes panel translation and reaction movement.", "Убирает сдвиг панелей и анимацию реакций.")}</small></span></div><Toggle checked={preferences.reducedMotion} label={ui("Reduce interface motion", "Меньше движения в интерфейсе")} onChange={(value) => update({ reducedMotion: value })} /></div>
            </section>

            <button className="reset-settings" type="button" onClick={reset}><RotateCcw aria-hidden="true" size={16} />{ui("Reset browser preferences", "Сбросить настройки браузера")}</button>
          </div>
        </div>
      </main>
    </AppShell>
  );
}

function Toggle({ checked, label, onChange }: { checked: boolean; label: string; onChange: (value: boolean) => void }) {
  return <button className="toggle" type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)}><span /></button>;
}
